import { apiClient } from './apiClient';

export interface ProductCategory {
  id: number;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  color?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface UnitType {
  id: number;
  name: string;
  short_name: string;
  type: 'countable' | 'weight' | 'volume' | 'length';
  is_active: boolean;
  is_default: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface CustomProductField {
  id: number;
  field_name: string;
  field_key: string;
  field_type: 'text' | 'number' | 'date' | 'textarea' | 'select' | 'checkbox';
  description?: string;
  options?: string[];
  default_value?: string;
  field_options?: string[];
  is_required: boolean;
  is_active: boolean;
  sort_order: number;
  validation_rules?: string;
  placeholder?: string;
  help_text?: string;
  created_at: string;
  updated_at: string;
}

export interface ShopTypeTemplate {
  id: number;
  name: string;
  slug: string;
  description?: string;
  icon?: string;
  default_categories?: string[];
  default_units?: string[];
  default_custom_fields?: string[];
  categories?: string[];
  units?: (string | UnitType)[];  // Can be string or UnitType object
  custom_fields?: (string | CustomProductField)[];  // Can be string or CustomProductField object
  categories_count?: number;
  units_count?: number;
  custom_fields_count?: number;
  is_active: boolean;
  is_default?: boolean;
  created_at: string;
  updated_at: string;
}

export interface TemplatesResponse {
  templates: ShopTypeTemplate[];
  current_template_id: number | null;
}

export interface CurrentConfiguration {
  categories: ProductCategory[];
  units: UnitType[];
  custom_fields: CustomProductField[];
}

// Product Categories API
export const categoryService = {
  getAll: async (activeOnly = false) => {
    return await apiClient.get<ProductCategory[]>(
      `/configuration/categories${activeOnly ? '?active_only=true' : ''}`
    );
  },

  getById: async (id: number) => {
    return await apiClient.get<ProductCategory>(`/configuration/categories/${id}`);
  },

  create: async (data: Partial<ProductCategory>) => {
    return await apiClient.post<{ message: string; category: ProductCategory }>(
      '/configuration/categories',
      data
    );
  },

  update: async (id: number, data: Partial<ProductCategory>) => {
    return await apiClient.put<{ message: string; category: ProductCategory }>(
      `/configuration/categories/${id}`,
      data
    );
  },

  delete: async (id: number) => {
    return await apiClient.delete<{ message: string }>(`/configuration/categories/${id}`);
  },

  updateSortOrder: async (categories: { id: number; sort_order: number }[]) => {
    return await apiClient.post<{ message: string }>(
      '/configuration/categories/sort-order',
      { categories }
    );
  },
};

// Unit Types API
export const unitTypeService = {
  getAll: async (activeOnly = false) => {
    return await apiClient.get<UnitType[]>(
      `/configuration/units${activeOnly ? '?active_only=true' : ''}`
    );
  },

  getById: async (id: number) => {
    return await apiClient.get<UnitType>(`/configuration/units/${id}`);
  },

  create: async (data: Partial<UnitType>) => {
    return await apiClient.post<{ message: string; unit: UnitType }>(
      '/configuration/units',
      data
    );
  },

  update: async (id: number, data: Partial<UnitType>) => {
    return await apiClient.put<{ message: string; unit: UnitType }>(
      `/configuration/units/${id}`,
      data
    );
  },

  delete: async (id: number) => {
    return await apiClient.delete<{ message: string }>(`/configuration/units/${id}`);
  },

  getDefault: async () => {
    return await apiClient.get<UnitType>('/configuration/units/default');
  },
};

// Custom Product Fields API
export const customFieldService = {
  getAll: async (activeOnly = false) => {
    return await apiClient.get<CustomProductField[]>(
      `/configuration/custom-fields${activeOnly ? '?active_only=true' : ''}`
    );
  },

  getById: async (id: number) => {
    return await apiClient.get<CustomProductField>(`/configuration/custom-fields/${id}`);
  },

  create: async (data: Partial<CustomProductField>) => {
    return await apiClient.post<{ message: string; field: CustomProductField }>(
      '/configuration/custom-fields',
      data
    );
  },

  update: async (id: number, data: Partial<CustomProductField>) => {
    return await apiClient.put<{ message: string; field: CustomProductField }>(
      `/configuration/custom-fields/${id}`,
      data
    );
  },

  delete: async (id: number) => {
    return await apiClient.delete<{ message: string }>(`/configuration/custom-fields/${id}`);
  },

  toggleActive: async (id: number) => {
    return await apiClient.post<{ message: string; field: CustomProductField }>(
      `/configuration/custom-fields/${id}/toggle`
    );
  },
};

// Shop Type Templates API
export const templateService = {
  getAll: async () => {
    return await apiClient.get<TemplatesResponse>('/configuration/templates');
  },

  getById: async (id: number) => {
    return await apiClient.get<ShopTypeTemplate>(`/configuration/templates/${id}`);
  },

  applyTemplate: async (id: number, replaceExisting = false) => {
    return await apiClient.post<{ message: string; template: string; categories_count: number; units_count: number }>(
      `/configuration/templates/${id}/apply`,
      { replace_existing: replaceExisting }
    );
  },

  getCurrentConfiguration: async () => {
    return await apiClient.get<CurrentConfiguration>('/configuration/current');
  },
};
